import { createFileRoute } from '@tanstack/react-router';
import { LegalDoc, type LegalSection } from '../components/legal/LegalDoc.js';
import { LEGAL } from '../lib/legal.js';

export const Route = createFileRoute('/privacy')({ component: Privacy });

/**
 * The privacy policy.
 *
 * Every claim in here is checkable against the code: the tables in `0001_init.sql` and
 * `0017_analytics.sql` are what we store, `packages/shared/src/analytics.ts` is the complete list
 * of events that can exist, and `deleteUserData` in `apps/api/src/db/queries/users.ts` is what
 * deletion does. If one of those changes, this file changes with it — the event list is an
 * allowlist precisely so that this page can be checked against it line by line.
 */
const SECTIONS: LegalSection[] = [
  {
    id: 'controller',
    heading: 'Who is responsible for your data',
    body: [
      `The controller of your personal data is ${LEGAL.entity}, a sole proprietorship run by ${LEGAL.proprietor} and registered in ${LEGAL.register}.`,
      `Address: ${LEGAL.address}. NIP: ${LEGAL.nip}. REGON: ${LEGAL.regon}.`,
      `For anything about your data, write to ${LEGAL.contactEmail}. We are a three person studio, so that mailbox reaches a person rather than a queue.`,
      'We have not appointed a data protection officer; we are not required to.',
    ],
  },
  {
    id: 'what-we-collect',
    heading: 'What we collect, and why',
    body: ['We collect as little as the thing will run on. Here is all of it.'],
    list: [
      'Account: your Discord user id, username, display name and avatar URL, and your role with us. Signing in with Discord is the only way to have an account, so we need all of it. Legal basis: performance of our contract with you.',
      'Sign in sessions: a SHA-256 hash of your session token (never the token), which platform the session is on, and when it was created, last used and expires. Legal basis: contract, and our legitimate interest in keeping accounts secure.',
      'Membership: your membership id, which plan you are on, whether it is valid, and when the current period ends. Legal basis: contract.',
      'Discord server link: your Discord user id and username, and when you signed in, so the bot can give you the supporter role. Legal basis: contract.',
      'Notifications: the notices we have sent you, which ones you have read, and which types you have muted. Legal basis: contract.',
      'Staff audit log: when somebody on our team publishes a build or changes a role, we record who did it and what they did. Legal basis: our legitimate interest in being able to account for staff actions.',
      'How the app is used: which screens you open, which games you install, launch and how long you play them, and when an install or a launch fails. These carry your account id while you are signed in. Legal basis: our legitimate interest in knowing which parts of the product work, and you can turn it off in Settings.',
      'How you arrived: the site that linked you, and the referral code in the address if there was one. Legal basis: our legitimate interest in knowing where our players come from.',
      'Inside a game: which level you started, which checkpoints you reached, where you died, and where you stopped playing. Not what you did, not where you were standing, not anything you typed. Legal basis: our legitimate interest in finding the places players get stuck. It stops when usage data is turned off.',
    ],
    after: [
      'Two things are deliberately absent from that list. We do not store your IP address, and we put no identifier on your device for counting. To count visits without one, our server turns your IP address and browser into a short code using a secret that is thrown away every night — so the same visitor cannot be recognised tomorrow, and the code cannot be turned back into a person by us or by anybody who took a copy of our data.',
      'Cloudflare Web Analytics measures traffic to the website in the same spirit: no cookie, no device storage, no identifier, and no way to follow anybody between sites. It does not run in the desktop or Android apps.',
    ],
  },
  {
    id: 'what-we-dont',
    heading: 'What we do not collect',
    body: ['Said plainly, because the absences matter as much as the list above.'],
    list: [
      'We never see your card, bank or billing details. Creem takes the payment as merchant of record and tells us only that a membership exists, which plan it is, and when its period ends. Your card number, your billing address and your tax country stay with Creem.',
      'We do not collect your address.',
      'We do not use a third-party product analytics service. What we measure is the list above, it is processed by our own code, and it is stored in our own Cloudflare account. No analytics company receives any of it.',
      'We do not track you across other websites and we do not build a profile of you. Nothing we collect is used to make an automated decision about you.',
      'The website carries no advertising tag of any kind.',
      'We do not read your Discord messages, your friends list or anything else in your Discord account. Signing in gives us your user id, username, display name and avatar, and nothing else.',
      'We do not record what happens inside a game beyond the named checkpoints described above. No screen capture, no voice, no chat, no positions and no inputs — nothing that could reconstruct a session.',
      'We do not read, upload or scan your files, your save games, or anything else on your device.',
      'We do not sell your data, and we do not share it for anybody else’s marketing.',
    ],
  },
  {
    id: 'processors',
    heading: 'Who else processes it',
    body: ['Three services, each doing one job:'],
    list: [
      'Cloudflare (Cloudflare, Inc.) — hosting, the database, file storage, the queues, and the two analytics products we use. Everything the app stores sits on Cloudflare’s network, in our own account.',
      'Discord (Discord Netherlands B.V. / Discord Inc.) — sign in, and putting you in our server with the right role. Signing in with Discord is the only way into GOOD BUILD, so Discord sees that you signed in. Discord is its own controller for your Discord account and has its own privacy notice.',
      'Creem (Creem B.V.) — taking the payment for a membership. Creem is the merchant of record and its own controller for what a sale needs: your email address, your card details, and the country you are taxed in. We send them nothing but an account reference, and they send us back only whether a membership is valid. Their privacy notice covers the payment itself.',
    ],
    after: [
      'Where a processor is outside the European Economic Area, the transfer is covered by the European Commission’s standard contractual clauses and that provider’s own safeguards.',
      'Beyond those three, we use no analytics, advertising or profiling service. We do not sell data and we do not share it with data brokers.',
    ],
  },
  {
    id: 'retention',
    heading: 'How long we keep it',
    list: [
      'Sign in sessions expire, and expired ones are deleted automatically by a job that runs once a day.',
      'Account and membership data is kept while your account exists.',
      'Notifications are kept while your account exists, and go when it is deleted.',
      'The staff audit log is kept for accountability. It records only the user id of whoever acted, and it survives account deletion so the record of what staff did is not erasable by the person who did it.',
      'Usage and in-game events are kept in full for three months, then deleted automatically by Cloudflare.',
      'Daily totals built from them — how many people visited, how many hours a game was played — are kept indefinitely. They are counts with nobody in them, which is why the three month limit above costs us nothing.',
      'The events that name your account — installs, launches, play sessions and refusals — are kept while your account exists. Deleting your account unlinks them: the row stays as an anonymous fact about a game and stops being about you.',
      'The secret used to count visits is deleted after two days. That is what makes an anonymous visit permanently anonymous.',
    ],
  },
  {
    id: 'your-rights',
    heading: 'Your rights',
    body: ['Under the GDPR you can ask us to:'],
    list: [
      'give you a copy of the personal data we hold about you;',
      'correct anything that is wrong;',
      'delete your data — see the next section, which you can also do yourself in one screen;',
      'restrict what we do with it, or object to processing we do on the basis of legitimate interest;',
      'hand it over in a portable form, or send it to somebody else;',
      'withdraw a consent you gave — unlinking Discord is exactly this.',
    ],
    after: [
      `Write to ${LEGAL.contactEmail}. We will answer within one month. We will not charge you, and we will not make you jump through hoops.`,
      `If you are not happy with our answer, you can complain to ${LEGAL.dpa.name}, ${LEGAL.dpa.address} (${LEGAL.dpa.url}), or to the supervisory authority in the EU country where you live.`,
    ],
  },
  {
    id: 'deletion',
    heading: 'Deleting your account',
    body: [
      'Your account screen has a Delete account button. It asks you to type your username, and then it runs immediately.',
      'Deleting removes every sign in session, your notifications and your notification settings, the link between your account and our Discord server, and any developer links. Your username and display name are replaced with an anonymous placeholder and your avatar is removed.',
      'The staff audit log keeps the user id of anyone on our team who took an action, which is what makes that log worth having. It holds no other detail about you.',
      'Your Discord account is separate and is not ours to delete. Deleting here removes our record of it and takes your role off our server; what you do with the Discord account itself is between you and Discord.',
    ],
  },
  {
    id: 'cookies',
    heading: 'Cookies and what is stored on your device',
    body: [
      'On the web we set one cookie: your sign in session. It is strictly necessary — without it you cannot stay signed in — so it does not need a consent banner, and there is no banner because there is nothing else to consent to.',
      'The desktop and Android apps do not use cookies. Your session token is kept in the operating system’s own secure storage.',
      'In your browser’s local storage we keep your app settings, the id of the last notification you were shown, a referral code for 30 days if you arrived through one, and any usage events that have not reached us yet because you were offline. Nothing there identifies you to anybody else.',
      'Nothing stored on your device is used to count or recognise you. There is no analytics cookie and no device id, which is why this page still has no consent banner on it.',
      'Turning off Share usage data in Settings stops the collection and deletes anything still waiting to be sent.',
    ],
  },
  {
    id: 'children',
    heading: 'Children',
    body: [
      'GOOD BUILD is not for children. You must be at least 16 to have an account, which is the age of digital consent in Poland.',
      `If you believe a child has an account with us, write to ${LEGAL.contactEmail} and we will remove it.`,
    ],
  },
  {
    id: 'security',
    heading: 'How we protect it',
    body: [
      'Session tokens are never stored as written: the database holds only a SHA-256 hash, so a copy of our database does not let anybody sign in as you. We store no third-party access tokens at all.',
      'No secret key reaches client code. Payment keys, storage credentials and the access pass signing key live as server secrets only.',
      'Downloads are served through short-lived signed links rather than public files, so a link cannot be shared and reused indefinitely.',
      'We cannot promise perfect security — nobody can — but if there is a breach that puts your rights at risk, we will tell you and the supervisory authority within the time the GDPR allows.',
    ],
  },
  {
    id: 'changes',
    heading: 'Changes to this policy',
    body: [
      'If we change what we collect or why, we will update this page and change the date at the top.',
      'If the change is significant — a new processor, a new purpose — we will tell you in the app before it takes effect.',
    ],
  },
  {
    id: 'contact',
    heading: 'Contact',
    body: [
      `${LEGAL.entity}`,
      `${LEGAL.address}`,
      `NIP ${LEGAL.nip} · REGON ${LEGAL.regon}`,
      `${LEGAL.contactEmail}`,
    ],
  },
];

function Privacy() {
  return (
    <LegalDoc
      eyebrow="Legal"
      title="Privacy policy."
      intro="What we collect, why, who else touches it, and how to get it back or get rid of it. Every claim here is checkable against the code that runs the service."
      sections={SECTIONS}
    />
  );
}
